import React, { useState, useEffect } from 'react';
import { Play, Pause, RotateCcw, X, Timer } from 'lucide-react';

interface WorkoutTimerModalProps {
  isOpen: boolean;
  onClose: () => void;
  workoutName?: string;
  totalDuration?: number;
}

const WorkoutTimerModal: React.FC<WorkoutTimerModalProps> = ({
  isOpen,
  onClose,
  workoutName = 'HIIT Circuit',
  totalDuration = 1200
}) => {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<'work' | 'rest'>('work');
  const [currentRound, setCurrentRound] = useState(1);
  const [workTime] = useState(45);
  const [restTime] = useState(15);
  const [totalRounds] = useState(8);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isRunning && isOpen) {
      interval = setInterval(() => {
        setTime(prevTime => {
          const newTime = prevTime + 1;
          
          if (currentPhase === 'work' && newTime >= workTime) {
            setCurrentPhase('rest');
            return 0;
          } else if (currentPhase === 'rest' && newTime >= restTime) {
            if (currentRound < totalRounds) {
              setCurrentRound(prev => prev + 1);
              setCurrentPhase('work');
              return 0;
            } else {
              setIsRunning(false);
              return newTime;
            }
          }
          
          return newTime;
        });
      }, 1000);
    }
    
    return () => clearInterval(interval);
  }, [isRunning, isOpen, currentPhase, currentRound, workTime, restTime, totalRounds]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseTime = (): number => {
    return currentPhase === 'work' ? workTime : restTime;
  };

  const getProgress = (): number => {
    return (time / getPhaseTime()) * 100;
  };

  const handlePlayPause = () => {
    setIsRunning(!isRunning);
  };

  const handleReset = () => {
    setTime(0);
    setIsRunning(false);
    setCurrentPhase('work');
    setCurrentRound(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-2xl w-full max-w-md p-8 relative"
        style={{ boxShadow: 'var(--shadow-xl)' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg transition-all duration-200"
          style={{
            background: 'var(--gradient-card-bg)',
            color: 'var(--color-text-muted)'
          }}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center space-y-6">
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6" style={{ color: 'var(--color-primary)' }} />
            <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
              {workoutName}
            </h2>
          </div>

          <div className="space-y-4">
            <div 
              className="text-6xl font-bold"
              style={{ 
                color: currentPhase === 'work' ? 'var(--color-primary)' : 'var(--color-accent)'
              }}
            >
              {formatTime(getPhaseTime() - time)}
            </div>
            
            <div className="space-y-2">
              <div 
                className="text-xl font-semibold uppercase tracking-wide"
                style={{ 
                  color: currentPhase === 'work' ? 'var(--color-primary)' : 'var(--color-accent)'
                }}
              >
                {currentPhase === 'work' ? 'WORK' : 'REST'}
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="h-3 rounded-full transition-all duration-1000 ease-linear"
                  style={{
                    width: `${getProgress()}%`,
                    background: currentPhase === 'work' 
                      ? 'var(--gradient-primary)' 
                      : 'var(--gradient-secondary)'
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center space-x-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <span>Round {currentRound} of {totalRounds}</span>
            <span>•</span>
            <span>{workTime}s work / {restTime}s rest</span>
          </div>

          <div className="flex justify-center space-x-4">
            <button
              onClick={handlePlayPause}
              className="p-4 rounded-full transition-all duration-200"
              style={{
                background: 'var(--gradient-primary)',
                color: 'var(--color-white)',
                boxShadow: 'var(--shadow-lg)'
              }}
            >
              {isRunning ? (
                <Pause className="h-8 w-8" />
              ) : (
                <Play className="h-8 w-8" />
              )}
            </button>
            
            <button
              onClick={handleReset}
              className="p-4 rounded-full transition-all duration-200"
              style={{
                background: 'var(--gradient-card-bg)',
                color: 'var(--color-text-muted)',
                boxShadow: 'var(--shadow-md)'
              }}
            >
              <RotateCcw className="h-8 w-8" />
            </button>
          </div>

          {currentRound === totalRounds && time >= getPhaseTime() && (
            <div 
              className="p-4 rounded-lg"
              style={{ 
                background: 'var(--gradient-primary)',
                color: 'var(--color-white)'
              }}
            >
              <p className="font-semibold">Workout Complete!</p>
              <p className="text-sm opacity-90">Great job finishing your HIIT circuit!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkoutTimerModal;
